import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Card,
  CardContent,
  Divider,
  Button,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  ArrowBack as ArrowBackIcon,
  Assignment as CaseIcon,
  Folder as ProjectIcon,
  Add as AddIcon,
  AccountBalanceWallet as WalletIcon,
} from '@mui/icons-material';
import { projectAPI, firmAPI } from '../services/api';
import { firmService, Firm } from '../services/firmService';
import { caseService } from '../services/caseService';
import { useAuth } from '../contexts/AuthContext';

interface Case {
  id: number;
  title: string;
  project_id: number;
  project?: { name: string };
  assigned_to?: { name: string };
  status: string;
  file_number?: string;
  created_at: string;
}

const FirmDashboard: React.FC = () => {
  const { firmId } = useParams<{ firmId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPmpAdmin = () => {
    if (!user?.roles) return false;
    return user.roles.some(role => role.slug === 'pmp_admin');
  };

  const isSiteAdmin = () => {
    if (!user?.roles) return false;
    return user.roles.some(role => role.slug === 'site_admin');
  };

  const isPmpAdminOrSiteAdmin = () => {
    return isPmpAdmin() || isSiteAdmin();
  };
  const hasClientManagerRole = () => {
    const roles = (user as any)?.roles || [];
    return roles.some((r: any) => r.slug === 'client_manager');
  };
  const isClientAdmin = () => {
    if (!user?.roles) return false;
    return user.roles.some((role) => 
      ['firm_admin', 'firm_main', 'client_admin', 'client_manager'].includes(role.slug)
    );
  };
  
  const [firm, setFirm] = useState<Firm | null>(null);
  const [recentCases, setRecentCases] = useState<Case[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [walletBalance, setWalletBalance] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (firmId) {
      fetchFirmData();
      fetchFirmProjects();
      fetchRecentCases();
    }
  }, [firmId]);

  const fetchFirmData = async () => {
    try {
      const response = await firmService.getFirmById(parseInt(firmId!));
      setFirm(response.data);
      // Fetch wallet balance after firm data is loaded if it's a client firm
      if (response.data.type === 'client') {
        fetchWalletBalance();
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const fetchRecentCases = async () => {
    try {
      const response = await caseService.getFirmCases(parseInt(firmId!));
      setRecentCases(response.data.data.slice(0, 10));
    } catch (err: any) {
      console.error('Error fetching cases:', err);
      setRecentCases([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchFirmProjects = async () => {
    try {
      const res = await projectAPI.getAll({ firm_id: firmId });
      setProjects(res.data.data.data || res.data.data || []);
    } catch (err) {
      setProjects([]);
    }
  };

  const fetchWalletBalance = async () => {
    // Only fetch wallet for client firms
    if (!firmId) return;
    
    try {
      const response = await firmAPI.getWalletBalance(parseInt(firmId!));
      if (response.data.success) {
        setWalletBalance(response.data.data);
      }
    } catch (err: any) {
      // Silently fail if wallet doesn't exist or user doesn't have access
      // This is expected for non-client firms or if wallet hasn't been created yet
      setWalletBalance(null);
    }
  };

  const getStatusChip = (status: string) => {
    switch (status.toLowerCase()) {
      case 'open':
          return <Chip label="Open" color="primary" size="small" />;
      case 'in_progress':
          return <Chip label="In Progress" color="warning" size="small" />;
      case 'completed':
          return <Chip label="Completed" color="success" size="small" />;
      case 'on_hold':
          return <Chip label="On Hold" color="secondary" size="small" />;
      case 'cancelled':
          return <Chip label="Cancelled" color="error" size="small" />;
      case 'awaiting_info':
          return <Chip label="Awaiting Info" color="info" size="small" />;
      case 'closed':
          return <Chip label="Closed" color="default" size="small" />;
      case 'estimate_approved':
          return <Chip label="Estimate Approved" color="success" size="small" />;
      case 'estimate_declined':
          return <Chip label="Estimate Declined" color="error" size="small" />;
      case 'estimate_sent':
          return <Chip label="Estimate Sent" color="info" size="small" />;
      case 'mrr_completed':
          return <Chip label="MRR Completed" color="success" size="small" />;
      case 'na':
          return <Chip label="N/A" color="default" size="small" />;
      case 'pending':
          return <Chip label="Pending" color="warning" size="small" />;
      case 'quality_check':
          return <Chip label="Quality Check" color="info" size="small" />;
      case 'quality_error':
          return <Chip label="Quality Error" color="error" size="small" />;
      case 'reworked':
          return <Chip label="Reworked" color="warning" size="small" />;
      case 'training':
          return <Chip label="Training" color="secondary" size="small" />;
      case 'voicemail':
          return <Chip label="Voicemail" color="default" size="small" />;
      case 'wip':
          return <Chip label="WIP" color="warning" size="small" />;
      case 'delivered':
          return <Chip label="Delivered" color="success" size="small" />;
      case 'mrr_delivered':
          return <Chip label="MRR Delivered" color="success" size="small" />;
      default:
          return <Chip label={status} size="small" />;
    }
  };

  const getTypeChip = (type: string) => {
    switch (type) {
      case 'client':
        return <Chip label="Client" color="primary" size="small" />;
      case 'vendor':
        return <Chip label="Vendor" color="secondary" size="small" />;
      default:
        return <Chip label={type} size="small" />;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!firm) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        Firm not found
      </Alert>
    );
  }

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          color="inherit"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            navigate('/firms');
          }}
          sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
        >
          <ArrowBackIcon sx={{ mr: 1 }} />
          Back to Firms
        </Link>
        <Typography color="text.primary">{firm.name}</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            {firm.name}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            {getTypeChip(firm.type)}
            {firm.status && (
              <Chip 
                label={firm.status.charAt(0).toUpperCase() + firm.status.slice(1)} 
                color={firm.status === 'approved' ? 'success' : firm.status === 'pending' ? 'warning' : 'error'} 
                size="small" 
              />
            )}
            {firm.category && (
              <Chip 
                label={firm.category === 'prepaid' ? 'Prepaid' : 'Postpaid'} 
                color={firm.category === 'prepaid' ? 'info' : 'default'} 
                size="small" 
              />
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="contained" color="primary" onClick={() => navigate(`/firms/${firmId}/members`)}>
            Members
          </Button>
          {(isPmpAdminOrSiteAdmin() || isClientAdmin()) && firm?.type === 'client' && (
            <Button 
              variant="contained" 
              color="success"
              onClick={() => navigate(`/firms/${firmId}/client-estimates`)}
            >
              Pending Estimates
            </Button>
          )}
          <Button variant="contained" color="primary" onClick={() => navigate(`/firms/${firmId}/legal`)}>
            Legal
          </Button>
          {isPmpAdminOrSiteAdmin() && firm?.type === 'client' && (
            <>
              <Button variant="contained" color="primary" onClick={() => navigate(`/firms/${firmId}/settings`)}>
                Settings
              </Button>
              <Button variant="contained" color="primary" onClick={() => navigate(`/firms/${firmId}/wallet`)}>
                Wallet
              </Button>
            </>
          )}
          <Button variant="contained" color="primary" onClick={() => navigate(`/firms/${firmId}/cases/new`)}>
            New Case
          </Button>
          {(isPmpAdmin() || hasClientManagerRole()) && firm?.type === 'client' && (
            <Button variant="contained" color="secondary" onClick={() => navigate(`/firms/${firmId}/vendor-assign`)}>
              Vendor Assignment
            </Button>
          )}
          {isPmpAdminOrSiteAdmin() && (
            <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => navigate(`/firms/${firmId}/projects/new`)}>
              Create Project
            </Button>
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 400px' }}>
          {/* Wallet Balance Card - Only for client firms */}
          {firm.type === 'client' && walletBalance && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}>
                    <WalletIcon />
                    Wallet Balance
                  </Typography>
                  <Button 
                    size="small" 
                    variant="outlined"
                    onClick={() => navigate(`/firms/${firmId}/wallet`)}
                  >
                    View Details
                  </Button>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Box>
                  <Typography variant="h4" color="primary.main" fontWeight="bold">
                    {walletBalance.currency} {parseFloat(walletBalance.balance).toFixed(2)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {firm.category === 'prepaid' ? 'Prepaid Client' : 'Postpaid Client'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}>
                <BusinessIcon />
                Organization Details
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Name
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {firm.name}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationIcon fontSize="small" />
                    Address
                  </Typography>
                  <Typography variant="body2">
                    {firm.address_1}
                  </Typography>
                  {firm.address_2 && (
                    <Typography variant="body2">
                      {firm.address_2}
                    </Typography>
                  )}
                  <Typography variant="body2">
                    {firm.city}, {firm.state} {firm.zip_code}
                  </Typography>
                  <Typography variant="body2">
                    {firm.country}
                  </Typography>
                </Box>

                {firm.contact_phone && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      Contact
                    </Typography>
                    <Typography variant="body2">
                      {firm.contact_phone}
                    </Typography>
                  </Box>
                )}

                {firm.website && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Website
                    </Typography>
                    <Typography variant="body2">
                      {firm.website}
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: '2 1 600px' }}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ProjectIcon />
                  Projects
                </Typography>
                <Button size="small" onClick={() => navigate(`/firms/${firmId}/projects`)}>View All</Button>
              </Box>
              <Divider sx={{ mb: 2 }} />
              {projects.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No projects for this firm</Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {projects.slice(0, 5).map((p) => (
                        <TableRow key={p.id} hover>
                          <TableCell>#{p.id}</TableCell>
                          <TableCell>{p.name}</TableCell>
                          <TableCell>
                            <Chip label={String(p.status).toUpperCase()} size="small" color={p.status === 'active' ? 'primary' : p.status === 'draft' ? 'default' : 'success'} />
                          </TableCell>
                          <TableCell align="right">
                            <Button size="small" onClick={() => navigate(`/firms/${firmId}/projects/${p.id}`)}>Open</Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'secondary.main' }}>
                  <CaseIcon />
                  Recent Cases
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => navigate(`/firms/${firmId}/projects`)}
                  >
                    View All Projects
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => navigate(`/firms/${firmId}/cases`)}
                  >
                    View All Cases
                  </Button>
                </Box>
              </Box>
              <Divider sx={{ mb: 2 }} />

              {recentCases.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No cases found for this firm
                  </Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Case ID</TableCell>
                        <TableCell>Case Title</TableCell>
                        <TableCell>Project</TableCell>
                        <TableCell>Assigned To</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>File Number</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recentCases.map((caseItem) => (
                        <TableRow key={caseItem.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              #{caseItem.id}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{ color: 'primary.main', cursor: 'pointer', textDecoration: 'underline' }}
                              onClick={() => navigate(`/firms/${firmId}/cases/${caseItem.id}`)}
                            >
                              {caseItem.title}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {caseItem.project?.name || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {caseItem.assigned_to?.name || 'Unassigned'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {getStatusChip(caseItem.status)}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {caseItem.file_number || 'N/A'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default FirmDashboard;
